import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

// Which groups the person had open, so the menu looks the same next time.
const OPEN_KEY = 'nav_open_groups';
const readOpen = () => {
  try { return JSON.parse(localStorage.getItem(OPEN_KEY)) || []; } catch { return []; }
};

export default function Sidebar({ page, setPage, collapsed = false }) {
  const { user, logout, isAdmin, can } = useAuth();
  const [open, setOpen] = useState(readOpen);

  // Every item's visibility rule is unchanged from when each was a flat entry —
  // grouping must not quietly take a screen away from anyone. `false` entries
  // are filtered out, and a group with nothing left in it is not rendered.
  const GROUPS = [
    { id:'work', label:'Daily Work', items: [
      can('tasks','view')          && ['tasks',        'Tasks',            '✓'],
      can('projects','view')       && ['projects',     'Projects',         '📁'],
      can('meetings','view')       && ['meetings',     'Meetings',         '👥'],
      can('incidents','view')      && ['incidents',    'Incidents',        '🚨'],
      can('remote_support','view') && ['remote',       'Remote Support',   '🖥'],
      can('surveillance','view')   && ['surveillance', 'Surveillance',     '📹'],
      can('calendar','view')       && ['calendar',     'IT Calendar',      '📅'],
      can('training','view')       && ['training',     'IT Team Training', '🎓'],
    ]},
    { id:'assets', label:'Assets & Infrastructure', items: [
      can('assets','view')       && ['assets',      'Assets',                   '□'],
      can('material_ack','view') && ['materialack', 'Material Acknowledgement', '✍'],
      can('rooms','view')        && ['rooms',       'Rooms',                    '🚪'],
      can('configs','view')      && ['configs',     'Device Config',            '⚙'],
      can('configs','view')      && ['servers',     'Servers',                  '🖧'],
      // IT Resources is the non-admin view of the same master screen.
      (!isAdmin && (can('it_resources','view') || can('staff','view') || can('students','view')))
                                 && ['master',      'IT Resources',             '☎️'],
    ]},
    { id:'onboard', label:'Onboarding', items: [
      can('onboarding','view') && ['onboarding',        'Staff Onboarding',   '🎓'],
      can('onboarding','view') && ['studentonboarding', 'Student Onboarding', '🎒'],
    ]},
    { id:'insight', label:'Insights', items: [
      (can('performance','view') || ['admin','it_head','it_lead'].includes(user?.role))
                              && ['performance', 'Performance & Compliance', '📊'],
      (can('mgmt_dashboard','view') || ['admin','it_head'].includes(user?.role))
                              && ['ceo',         'Management Dashboard',     '🏫'],
      can('problems','view')  && ['problems',    'Problems',                 '🧩'],
      can('vendors','view')   && ['vendormgmt',  'Vendor Management',        '🤝'],
      can('feedback','view')  && ['feedback',    'Ticket Feedback',          '⭐'],
      can('ai_logs','view')   && ['ailogs',      'AI Logs',                  '🤖'],
    ]},
    { id:'gov', label:'Governance', items: [
      can('policies','view')      && ['policies',      'Policies',        '📋'],
      can('documents','view')     && ['documents',     'Documents',       '📄'],
      can('approvals','view')     && ['approvals',     'Approval Center', '✅'],
      can('announcements','view') && ['announcements', 'Announcements',   '📢'],
    ]},
    { id:'finance', label:'Finance', items: [
      can('fee_dashboard','view') && ['feedash',   'Fee Dashboard',     '💰'],
      can('fee_upload','view')    && ['feeupload', 'Fee Report Upload', '⬆'],
    ]},
    { id:'admin', label:'Administration', items: [
      can('users','view')    && ['users',       'Users',               '👤'],
      can('roles','view')    && ['roles',       'Roles',               '🔐'],
      can('roles','view')    && ['permissions', 'Permissions',         '🔑'],
      isAdmin                && ['master',      'Master Data',         '🗂'],
      can('logs','view')     && ['logs',        'System Logs',         '📜'],
      can('activity','view') && ['activity',    'User Activity',       '👥'],
      can('backup','view')   && ['backup',      'Backup',              '💾'],
      can('recycle','view')  && ['recycle',     'Recycle Bin',         '🗑'],
      can('email','view')    && ['mail',        'Email Notifications', '📧'],
      can('settings','view') && ['settings',    'Settings',            '⚙️'],
    ]},
  ].map(g => ({ ...g, items: g.items.filter(Boolean) }))
   .filter(g => g.items.length);

  // Whichever group holds the current page is open, always — otherwise
  // navigating from elsewhere (a dashboard tile, say) lands you on a screen
  // whose menu entry is hidden inside a closed group.
  const activeGroup = GROUPS.find(g => g.items.some(([id]) => id === page))?.id;
  useEffect(() => {
    if (!activeGroup) return;
    // Functional form so the effect needs no dependency on `open` — it reads
    // the current value rather than one captured when the page changed.
    setOpen(prev => {
      if (prev.includes(activeGroup)) return prev;
      const next = [...prev, activeGroup];
      try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [activeGroup]);

  const toggle = useCallback(id => {
    setOpen(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      try { localStorage.setItem(OPEN_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const navItem = (id, label, icon) => (
    <button key={id} onClick={() => setPage(id)} className={`sidebar-link${page===id ? ' active' : ''}`}>
      <span className="nav-ico">{icon}</span>{label}
    </button>
  );

  return (
    <aside className={`sidebar${collapsed ? ' collapsed' : ''}`} style={{
      width:'220px', borderRight:'1px solid var(--nav-active)',
      display:'flex', flexDirection:'column', flexShrink:0
    }}>
      {/* Logo */}
      <div style={{ padding:'16px', borderBottom:'1px solid var(--nav-active)', display:'flex', alignItems:'center', gap:10 }}>
        <img src={`${process.env.PUBLIC_URL || ''}/it-logo.png`} alt="IT"
          style={{ height:36, width:'auto', objectFit:'contain', background:'#fff', borderRadius:6, padding:3 }} />
        <div>
          <div style={{ fontWeight:700, fontSize:14, color:'#fff', letterSpacing:'-0.3px' }}>IT Portal</div>
          <div style={{ fontSize:10, color:'var(--nav-text2)' }}>Management System</div>
        </div>
      </div>

      {/* Nav — every item is permission-driven (admin sees all via can()===true) */}
      <nav style={{ flex:1, padding:'12px 8px', overflowY:'auto' }}>

        {/* Outside any group: the three screens people open constantly. Putting
            them behind a dropdown would cost a click every single time. */}
        {navItem('dashboard', 'Dashboard', '📊')}
        {/* The IT Head's own console: what needs a person today, across every
            campus. Admin / IT Head only — for campus staff it would show
            approvals and comparisons that are not theirs to act on. */}
        {(can('command_center','view') || ['admin','it_head'].includes(user?.role))
          && navItem('command', 'Command Center', '🛰')}
        {/* Policy acknowledgement applies to everyone, so it needs no permission */}
        {navItem('mypolicies', 'My Policies', '📋')}

        {GROUPS.map(g => {
          const isOpen = open.includes(g.id);
          const hasActive = g.items.some(([id]) => id === page);
          return (
            <div key={g.id} style={{ marginTop:10 }}>
              <button onClick={() => toggle(g.id)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:6,
                  background:'none', border:'none', cursor:'pointer', padding:'4px 6px',
                  fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em',
                  color: hasActive ? 'var(--nav-text)' : 'var(--nav-text2)',
                  opacity: hasActive ? 1 : 0.8 }}>
                <span style={{ display:'inline-block', width:10, fontSize:9,
                  transform: isOpen ? 'rotate(90deg)' : 'none', transition:'transform .12s' }}>▶</span>
                <span style={{ flex:1, textAlign:'left' }}>{g.label}</span>
                {/* A closed group that contains the current page still says so. */}
                {!isOpen && hasActive && <span style={{ fontSize:14, lineHeight:1 }}>•</span>}
              </button>
              {isOpen && g.items.map(([id, label, icon]) => navItem(id, label, icon))}
            </div>
          );
        })}
      </nav>

      {/* User info */}
      <div style={{ padding:'12px 16px', borderTop:'1px solid var(--nav-active)' }}>
        <div style={{ fontSize:12, fontWeight:600, marginBottom:2, color:'#fff' }}>{user?.full_name}</div>
        <div style={{ fontSize:11, color:'var(--nav-text2)', marginBottom:2 }}>
          {user?.role === 'admin' ? '👑 Admin' : user?.role === 'it_head' ? '🌐 IT Head'
            : user?.role === 'it_lead' ? '🎯 IT Lead' : '👤 IT Admin'}
        </div>
        {user?.permission_role_name && (
          <div style={{ fontSize:10, color:'#cdd6ee', marginBottom:8 }}>{user.permission_role_name}</div>
        )}
        <button onClick={logout} className="btn btn-sm" style={{ width:'100%', justifyContent:'center', marginTop:6, background:'var(--nav-active)', color:'#fff' }}>
          Sign out
        </button>
      </div>
    </aside>
  );
}
